'use client';

import { useState } from 'react';

const INITIAL_STATE = {
  weight: 170,
  height: 70,
};

const PRESETS = {
  conservative: {
    weight: 150,
    height: 68,
  },
  moderate: {
    weight: 170,
    height: 70,
  },
  aggressive: {
    weight: 200,
    height: 72,
  },
};

export default function CalculatorBMI() {
  const [weight, setWeight] = useState(INITIAL_STATE.weight);
  const [height, setHeight] = useState(INITIAL_STATE.height);
  const [isCalculating, setIsCalculating] = useState(false);

  const handleChange = (setter: (v: number) => void, value: number) => {
    setter(value);
    setIsCalculating(true);
    setTimeout(() => setIsCalculating(false), 800);
  };

  const bmi = (weight / (height * height)) * 703;
  
  const getCategory = () => {
    if (bmi < 18.5) return { text: 'Underweight', color: 'text-blue-600', bg: 'bg-blue-500' };
    if (bmi < 25) return { text: 'Normal', color: 'text-green-600', bg: 'bg-green-500' };
    if (bmi < 30) return { text: 'Overweight', color: 'text-orange-600', bg: 'bg-orange-500' };
    return { text: 'Obese', color: 'text-red-600', bg: 'bg-red-500' };
  };

  const category = getCategory();

  const handleReset = () => {
    setWeight(INITIAL_STATE.weight);
    setHeight(INITIAL_STATE.height);
  };

  const handlePreset = (preset: keyof typeof PRESETS) => {
    const values = PRESETS[preset];
    setWeight(values.weight);
    setHeight(values.height);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
        {isCalculating && (
          <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2">
              <div className="w-5 h-5 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-emerald-700 font-semibold">Calculating...</span>
            </div>
          </div>
        )}

        {/* Preset and Reset Buttons */}
        <div className="mb-6 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => handlePreset('conservative')}
              className="px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg font-semibold text-sm transition-all border border-blue-200"
            >
              Conservative
            </button>
            <button
              onClick={() => handlePreset('moderate')}
              className="px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg font-semibold text-sm transition-all border border-indigo-200"
            >
              Moderate
            </button>
            <button
              onClick={() => handlePreset('aggressive')}
              className="px-3 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg font-semibold text-sm transition-all border border-purple-200"
            >
              Aggressive
            </button>
          </div>
          <button
            onClick={handleReset}
            className="w-full px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-semibold text-sm transition-all border border-slate-300"
          >
            Reset to Default
          </button>
        </div>

        <div className="space-y-6 mb-8">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Weight (lbs)</label>
            <input
              type="number"
              value={weight}
              onChange={(e) => handleChange(setWeight, Number(e.target.value))}
              className="w-full px-3 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Height (inches)</label>
            <input
              type="number"
              value={height}
              onChange={(e) => handleChange(setHeight, Number(e.target.value))}
              className="w-full px-3 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
            />
          </div>
        </div>

        <div className={`bg-gradient-to-br ${category.bg} rounded-xl p-6 text-white text-center mb-4`}>
          <div className="text-sm font-semibold uppercase tracking-wider opacity-90 mb-2">Your BMI</div>
          <div className="text-5xl font-bold mb-2">{bmi.toFixed(1)}</div>
          <div className="text-lg font-semibold">{category.text}</div>
        </div>

        <div className="bg-slate-50 rounded-lg p-4">
          <div className="text-sm text-slate-700 space-y-1">
            <div className="flex justify-between"><span>Underweight:</span><span className="font-semibold">&lt; 18.5</span></div>
            <div className="flex justify-between"><span>Normal:</span><span className="font-semibold">18.5 - 24.9</span></div>
            <div className="flex justify-between"><span>Overweight:</span><span className="font-semibold">25 - 29.9</span></div>
            <div className="flex justify-between"><span>Obese:</span><span className="font-semibold">≥ 30</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}